import { Link } from '@backstage/core-components';
import { makeStyles } from '@material-ui/core';
import { useNotificationsApi } from '@backstage/plugin-notifications';
import { useLocation, useNavigate } from 'react-router-dom';
import { HoneWordmark } from '../../components/HoneWordmark';
import {
  BellIcon,
  ChevronRightIcon,
  QuestionCircleIcon,
} from '../../components/icons';
import { crumbForPath } from './PortalHeader';
import { TECHDOCS_HEADER_SLOT_ID } from './techDocsHeaderSlot';

const useStyles = makeStyles({
  /*
   * The top app bar is part of the scrolling content, not a sticky overlay
   * (mocks 3a/3c/3e put it there). A sticky, opaque bar would slide over the
   * page's own header as it scrolled — the large entity title read as clipped
   * under it. The safe-area inset replaces the mock's status-bar gap.
   */
  root: {
    flex: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    minHeight: 44,
    padding: '10px 20px',
    paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)',
  },
  // Top-level pages (mock 3a/3d): logo + bell blend into the panel-tier surface.
  rootTopLevel: {
    background: 'var(--bui-bg-neutral-2)',
  },
  // Detail pages (mock 3c/3e): white band with a hairline and back affordance.
  rootDetail: {
    gap: 6,
    paddingLeft: 12,
    background: 'var(--bui-white)',
    borderBottom: '1px solid var(--hone-portal-hairline-soft)',
  },
  logoLink: { display: 'inline-flex', alignItems: 'center', gap: 10 },
  // The wordmark fills with `currentColor`; pin it to the primary foreground so
  // it goes light in dark mode and dark in light rather than tracking link ink.
  logoMark: { height: 13, width: 'auto', color: 'var(--bui-fg-primary)' },
  divider: {
    width: 1,
    height: 13,
    background: 'var(--hone-portal-hairline-strong)',
    flex: 'none',
  },
  logoLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--bui-fg-primary)',
  },
  spacer: { flex: 1 },
  // 44px tap target around each affordance (bell, back).
  tap: {
    width: 44,
    height: 44,
    marginRight: -12,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    border: 'none',
    background: 'none',
    padding: 0,
    cursor: 'pointer',
    color: 'inherit',
  },
  backTap: { marginRight: 0, marginLeft: -12 },
  // The detail bar's own actions cluster — Help, the TechDocs settings slot,
  // and the bell — opposite the back + breadcrumb, mirroring the desktop
  // header's right-aligned trio. Only the trailing icon (the bell, as on the
  // top-level bar) pulls in with `.tap`'s own -12px; a middle icon keeps the
  // full 44px tap target without hugging either edge.
  detailActions: {
    flex: 'none',
    display: 'flex',
    alignItems: 'center',
  },
  midTap: {
    width: 44,
    height: 44,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'none',
    padding: 0,
  },
  bell: { width: 18, height: 18, opacity: 0.55, display: 'block' },
  help: { width: 18, height: 18, opacity: 0.55, display: 'block' },
  back: {
    width: 15,
    height: 15,
    opacity: 0.55,
    display: 'block',
    transform: 'rotate(180deg)',
  },
  dot: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: '#d93036', // status error; no Helix token (matches hone-portal.css)
    boxShadow: '0 0 0 1.5px var(--bui-bg-neutral-2)',
  },
  crumb: {
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--bui-fg-primary)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

// A detail page is any route deeper than its section root (e.g. an entity under
// /catalog or a doc under /docs). Those get the back + breadcrumb variant; the
// section roots and flat pages get the logo + bell variant (mock 3a/3d vs 3c/3e).
function isDetailRoute(pathname: string): boolean {
  return pathname.split('/').filter(Boolean).length > 1;
}

// The mobile top app bar. Replaces the desktop breadcrumb bar below the phone
// breakpoint; the shell chooses between this and `PortalHeader`.
export function MobileTopBar() {
  const classes = useStyles();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { value, error } = useNotificationsApi(api => api.getStatus(), [
    pathname,
  ]);
  const unread = !error && (value?.unread ?? 0) > 0;

  if (isDetailRoute(pathname)) {
    return (
      <header className={`${classes.root} ${classes.rootDetail}`}>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={`${classes.tap} ${classes.backTap}`}
          aria-label="Back"
        >
          <ChevronRightIcon className={classes.back} />
        </button>
        <span className={classes.crumb}>{crumbForPath(pathname)}</span>
        <span className={classes.spacer} />
        <span className={classes.detailActions}>
          {/* Empty until the TechDocs reader is on screen — see
              `techDocsHeaderSlot.ts`. No intrinsic size, so a non-TechDocs
              detail page (the common case) shows Help + the bell with no
              gap where the cog would sit. */}
          <span id={TECHDOCS_HEADER_SLOT_ID} />
          <Link
            to="/documentation"
            underline="none"
            title="Help and documentation"
            aria-label="Help"
            className={classes.midTap}
          >
            <QuestionCircleIcon className={classes.help} />
          </Link>
          <Link
            to="/notifications"
            underline="none"
            className={classes.tap}
            title="Notifications"
            aria-label="Notifications"
          >
            <BellIcon className={classes.bell} />
            {unread && <span className={classes.dot} />}
          </Link>
        </span>
      </header>
    );
  }

  return (
    <header className={`${classes.root} ${classes.rootTopLevel}`}>
      <Link to="/" underline="none" className={classes.logoLink} aria-label="Home">
        <HoneWordmark className={classes.logoMark} />
        <span className={classes.divider} />
        <span className={classes.logoLabel}>Portal</span>
      </Link>
      <span className={classes.spacer} />
      <Link
        to="/notifications"
        underline="none"
        className={classes.tap}
        title="Notifications"
        aria-label="Notifications"
      >
        <BellIcon className={classes.bell} />
        {unread && <span className={classes.dot} />}
      </Link>
    </header>
  );
}
